import './dialogs.css';
import { Badge, Button, Checkbox, Popconfirm, Segmented, Tag, Tooltip } from 'antd';
import {
    ArrowDownOutlined, ArrowUpOutlined, CheckSquareOutlined, ContactsOutlined, DeleteOutlined, MessageOutlined,
    MinusSquareOutlined, TeamOutlined, ReloadOutlined, UserOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { serverFetch, fileUrl } from '@server';
import { showError } from '@extensions/utils';
import { useDicts, channelVidName } from '@extensions/dicts';
import { useUrlFilters } from '@extensions/url-filters';
import { PageTitle } from '@extensions/layout';
import FilterBar from '@controls/filter-bar/filter-bar';
import CardList, { CardRow } from '@controls/card-list/card-list';
import dialogSignalR from '../../services/dialog-signalr';
import DialogDrawer from './dialog';
import { dialogTitle, isDialogUnread, formatDialogTime } from './dialog-utils';
import { formatPhone } from '@extensions/contact-mask';
import Contragent from '../contragents/contragent';
import Task from '../tasks/task';

// Фильтры в URL: ?tab=&q=&user=&channel=&contragent=&archive=1&hidden=1
const FILTER_SPEC = {
    tab: { param: 'tab', type: 'str', empty: 'all' },
    q: { param: 'q', type: 'str', empty: '' },
    user: { param: 'user', type: 'int', empty: 0 },
    channel: { param: 'channel', type: 'int', empty: 0 },
    contragent: { param: 'contragent', type: 'int', empty: 0 },
    archive: { param: 'archive', type: 'bool', empty: false },
    hidden: { param: 'hidden', type: 'bool', empty: false },
};

const TABS = [
    { value: 'all', label: 'Все' },
    { value: 'awaiting', label: 'Ждут ответа' },
    { value: 'mine', label: 'Мои' },
    { value: 'unread', label: 'Непрочитанные' },
];

const notifyChanged = () => window.dispatchEvent(new Event('dialogs-changed'));

const Dialogs = () => {
    const user = useSelector((s) => s.user);
    const { dicts } = useDicts();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilter] = useUrlFilters(FILTER_SPEC, 'dialogFilters');
    const [items, setItems] = useState([]);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editDialog, setEditDialog] = useState(null);
    const [editContragent, setEditContragent] = useState(null);
    const [editTask, setEditTask] = useState(null);
    const [checked, setChecked] = useState([]);
    const lastChecked = useRef(null);
    const editDialogRef = useRef(null);
    const realtimeHandler = useRef(null);
    const dialogChangedHandler = useRef(null);
    const messageDeletedHandler = useRef(null);

    const load = useCallback(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (filters.q) qs.append('q', filters.q);
        if (filters.user) qs.append('userId', filters.user);
        if (filters.channel) qs.append('channelId', filters.channel);
        if (filters.contragent) qs.append('contragentId', filters.contragent);
        if (filters.archive) qs.append('deleted', 'true');
        if (filters.hidden) qs.append('showHidden', 'true');
        if (filters.tab === 'awaiting') qs.append('awaiting', 'true');
        if (filters.tab === 'unread') qs.append('isNew', 'true');
        if (filters.tab === 'mine' && user?.userId) qs.append('userId', user.userId);
        serverFetch(`dialog?${qs.toString()}`)
            .then(setItems)
            .catch(showError)
            .finally(() => setLoading(false));
    }, [filters, user?.userId]);

    useEffect(() => { load(); }, [load]);

    // Сброс отметки удалённых/выгруженных диалогов
    useEffect(() => {
        const ids = new Set(items.map((i) => i.id));
        setChecked((c) => c.filter((id) => ids.has(id)));
    }, [items]);

    useEffect(() => { editDialogRef.current = editDialog; }, [editDialog]);

    useEffect(() => {
        serverFetch('dialog/channels').then(setChannels).catch(() => {});
    }, []);

    // Открыть диалог по ?id=
    useEffect(() => {
        const id = Number(searchParams.get('id'));
        if (id > 0) setEditDialog(id);
    }, [searchParams]);

    // Точечное обновление диалога в списке по событию SignalR
    const refreshDialog = useCallback((dialogId) => {
        serverFetch(`dialog/${dialogId}`)
            .then((d) => setItems((list) => {
                const idx = list.findIndex((x) => x.id === d.id);
                if (idx < 0) return list;
                const next = [...list];
                next[idx] = d;
                return next;
            }))
            .catch(() => load());
        notifyChanged();
    }, [load]);

    // SignalR: новые сообщения и изменения диалогов компании
    useEffect(() => {
        const sessionId = localStorage.getItem('sessionId');
        if (!sessionId) return undefined;
        let alive = true;
        dialogSignalR.joinCompany(sessionId, {
            onReceiveMessage: (dto) => {
                if (!alive) return;
                realtimeHandler.current?.(dto);
                refreshDialog(dto.dialogId);
                dialogChangedHandler.current?.(dto);
            },
            onMessageDeleted: (dto) => {
                if (!alive) return;
                messageDeletedHandler.current?.(dto);
                refreshDialog(dto.dialogId);
            },
            onDialogChanged: (dto) => {
                if (!alive) return;
                refreshDialog(dto.dialogId);
                dialogChangedHandler.current?.(dto);
            },
            onDialogDeleted: (dto) => {
                if (!alive) return;
                setItems((list) => list.filter((x) => x.id !== dto.dialogId));
                notifyChanged();
                if (editDialogRef.current === dto.dialogId) {
                    setEditDialog(null);
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete('id');
                        return next;
                    }, { replace: true });
                }
            },
        }).catch(() => {});
        return () => { alive = false; };
    }, [refreshDialog, setSearchParams]);

    const closeDialog = () => {
        setEditDialog(null);
        if (searchParams.get('id')) {
            searchParams.delete('id');
            setSearchParams(searchParams, { replace: true });
        }
        load();
    };

    const clearState = (id, action, e) => {
        e.preventDefault();
        e.stopPropagation();
        serverFetch(`dialog/${id}/${action}`, { method: 'PUT' })
            .then(() => load())
            .catch(showError);
    };

    const tagById = useMemo(() => new Map(dicts.tags.map((t) => [t.id, t])), [dicts.tags]);

    const filterDefs = useMemo(() => [
        { key: 'user', label: 'Менеджер', emptyValue: 0, options: dicts.users.map((u) => ({ value: u.id, label: u.name })) },
        { key: 'channel', label: 'Канал', emptyValue: 0, options: channels.map((c) => ({ value: c.id, label: `${channelVidName(c.vid)} — ${c.name}` })) },
        { key: 'archive', label: 'Архив', emptyValue: false, options: [{ value: true, label: 'в архиве' }] },
        { key: 'hidden', label: 'Скрытые линии', emptyValue: false, options: [{ value: true, label: 'показывать' }] },
    ], [dicts.users, channels]);

    // ===== Мультивыбор (только суперадмин) =====
    const rowIds = items.map((r) => r.id);
    const allChecked = rowIds.length > 0 && rowIds.every((id) => checked.includes(id));

    // Отметка диалога: Shift — диапазон от последнего отмеченного
    const check = (id, on, shift) => {
        const from = rowIds.indexOf(lastChecked.current);
        const to = rowIds.indexOf(id);
        const ids = shift && from >= 0 && to >= 0
            ? rowIds.slice(Math.min(from, to), Math.max(from, to) + 1)
            : [id];
        setChecked((c) => (on ? [...new Set([...c, ...ids])] : c.filter((x) => !ids.includes(x))));
        lastChecked.current = id;
    };

    const onCardClick = (r, e) => {
        if (user?.isSuperadmin && (e.shiftKey || e.ctrlKey || e.metaKey)) {
            check(r.id, e.shiftKey ? true : !checked.includes(r.id), e.shiftKey);
        } else {
            setEditDialog(r.id);
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('id', String(r.id));
                return next;
            }, { replace: true });
        }
    };

    const deleteChecked = () => {
        serverFetch('dialog/delete-many', { method: 'POST', bodyData: { ids: checked } })
            .then(() => { setChecked([]); notifyChanged(); load(); })
            .catch(showError);
    };

    return (
        <div>
            <div className="page-toolbar">
                <PageTitle>Диалоги</PageTitle>
                <Segmented
                    options={TABS}
                    value={filters.tab || 'all'}
                    onChange={(v) => setFilter({ tab: v })}
                />
                <FilterBar
                    filters={filterDefs}
                    value={filters}
                    onChange={setFilter}
                    searchValue={filters.q}
                    onSearch={(v) => setFilter({ q: v })}
                    placeholder="Поиск диалогов"
                />
                <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
                {user?.isSuperadmin && (
                    <Tooltip
                        title={allChecked
                            ? 'Снять отметку со всех'
                            : 'Отметить все в списке. Shift+клик по карточке — диапазон, Ctrl/⌘+клик — один диалог'}
                    >
                        <Button
                            icon={allChecked ? <MinusSquareOutlined /> : <CheckSquareOutlined />}
                            disabled={!rowIds.length}
                            onClick={() => setChecked(allChecked
                                ? checked.filter((id) => !rowIds.includes(id))
                                : [...new Set([...checked, ...rowIds])])}
                        />
                    </Tooltip>
                )}
                {user?.isSuperadmin && checked.length > 0 && (
                    <>
                        <span className="page-toolbar-info">Выбрано: {checked.length}</span>
                        <Popconfirm
                            title={`Удалить выбранные диалоги (${checked.length}) безвозвратно вместе с сообщениями и файлами?`}
                            okText="Удалить"
                            cancelText="Отмена"
                            okButtonProps={{ danger: true }}
                            onConfirm={deleteChecked}
                        >
                            <Button danger icon={<DeleteOutlined />}>Удалить</Button>
                        </Popconfirm>
                    </>
                )}
            </div>
            <CardList
                items={items}
                loading={loading}
                pageSize={30}
                emptyText="Диалогов нет"
                onClick={onCardClick}
                rowStyle={(r) => (r.deleted ? { opacity: 0.55 } : undefined)}
                cardProps={(r) => {
                    const names = [];
                    if (r.missed && !r.deleted) names.push('dlg-missed');
                    if (r.incoming && !r.deleted) names.push('dlg-incoming');
                    if (checked.includes(r.id)) names.push('dlg-checked');
                    if (r.id === editDialog) names.push('dlg-open');
                    return names.length ? { className: names.join(' ') } : {};
                }}
                renderTitle={(r) => (
                    <>
                        {user?.isSuperadmin && (
                            <span onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={checked.includes(r.id)}
                                    onChange={(e) => check(r.id, e.target.checked, e.nativeEvent.shiftKey)}
                                />
                            </span>
                        )}
                        {r.channelIconFileId
                            ? <img className="channel-icon" src={fileUrl(r.channelIconFileId, true)} alt="" />
                            : <MessageOutlined />}
                        {isDialogUnread(r) && <Badge status="processing" />}
                        {r.isGroup ? <Tooltip title="Групповой чат"><TeamOutlined className="dlg-group-icon" /></Tooltip> : null}
                        <span className={isDialogUnread(r) ? 'dlg-title-unread' : undefined}>{dialogTitle(r)}</span>
                        {r.missed && !r.deleted ? (
                            <span onClick={(e) => e.stopPropagation()}>
                                <Tag color="error" closable onClose={(e) => clearState(r.id, 'clear-missed', e)}>
                                    Пропущенный
                                </Tag>
                            </span>
                        ) : null}
                        {r.incoming && !r.deleted ? (
                            <span onClick={(e) => e.stopPropagation()}>
                                <Tag color="error" closable onClose={(e) => clearState(r.id, 'clear-incoming', e)}>
                                    Входящее
                                </Tag>
                            </span>
                        ) : null}
                        <Tag>
                            {channelVidName(r.channelVid)}{r.channelName ? ` · ${r.channelName}` : ''}
                        </Tag>
                        {r.line ? <Tag>{formatPhone(r.line)}</Tag> : null}
                        {r.lineHidden ? <Tag>скрытая линия</Tag> : null}
                        {(r.tags ?? []).map((id) => {
                            const t = tagById.get(id);
                            return t ? (
                                <Tag key={id} color={t.bgColor || undefined} style={{ color: t.textColor || undefined }}>{t.name}</Tag>
                            ) : null;
                        })}
                        <span className="card-list-muted">{formatDialogTime(r.lastUpdated)}</span>
                    </>
                )}
                renderBody={(r) => (
                    <>
                        {r.lastMessageText && (
                            <CardRow
                                className="card-list-muted card-list-wide"
                                icon={r.lastMessageDirection === 1 ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
                            >
                                {r.lastMessageText.length > 120 ? `${r.lastMessageText.slice(0, 120)}…` : r.lastMessageText}
                            </CardRow>
                        )}
                        {r.userName && <CardRow icon={<UserOutlined />}>{r.userName}</CardRow>}
                        {r.contragentId && (
                            <CardRow icon={<ContactsOutlined />}>
                                <Button
                                    type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                    onClick={(e) => { e.stopPropagation(); setEditContragent(r.contragentId); }}
                                >
                                    {r.contragentName || `#${r.contragentId}`}
                                </Button>
                            </CardRow>
                        )}
                    </>
                )}
            />
            {editDialog !== null && (
                <DialogDrawer
                    id={editDialog}
                    onClose={closeDialog}
                    onChanged={() => { load(); notifyChanged(); }}
                    onOpenContragent={(cid) => setEditContragent(cid)}
                    onCreatedTask={(t) => setEditTask(t.id)}
                    registerRealtime={(fn) => { realtimeHandler.current = fn; }}
                    registerDialogChanged={(fn) => { dialogChangedHandler.current = fn; }}
                    registerMessageDeleted={(fn) => { messageDeletedHandler.current = fn; }}
                />
            )}
            {editContragent !== null && (
                <Contragent id={editContragent} onClose={() => setEditContragent(null)} />
            )}
            {editTask !== null && (
                <Task id={editTask} onClose={() => setEditTask(null)} />
            )}
        </div>
    );
};

export default Dialogs;
